import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { CouponButton } from 'src/features/Coupon/Regular/CouponButton'
import { CouponListDialog } from 'src/features/Coupon/Regular/CouponListDialog'
import { useDynamicConstants } from 'src/services/constants'
import { localeAtom } from 'src/stores/locale'
import { formatPrice, useCurrency } from 'src/utils/currency'
import type { ExtraPackage } from '../api/types'
import { PackSectionHeader } from '../components/PackSectionHeader'
import { RegularPackCard } from '../components/RegularPackCard'
import { getEventDisplayName } from '../domain/getEventDisplayName'
import { sortBoostsPriority } from '../domain/sortBoostsPriority'
import { formatLocalizedNumber } from '../utils/formatLocalizedNumber'

interface RegularPackSectionProps {
  packs: ExtraPackage[]
  showingSuccessId: string | null
  currentTier: number
  selectedTier: number
  onPurchase: (pack: ExtraPackage) => void
  onRequireUpgradeClick?: () => void
}

export function RegularPackSection(props: RegularPackSectionProps) {
  const { t } = useTranslation(['membership'])
  const [currency] = useCurrency()
  const locale = useAtomValue(localeAtom)
  const { data: constants } = useDynamicConstants()

  if (props.packs.length === 0) return null

  const title = t('membership:extra-pack.groups.regular.label')

  // event display name is temporary for 2026 new year event, not sure need to keep in future
  // remove it after confirmed.
  // getEventDisplayName(props.packs) ?? t('membership:extra-pack.groups.regular.label')

  return (
    <>
      <PackSectionHeader>{title}</PackSectionHeader>
      <CouponButton />
      {constants?.couponSystem?.enabled && <CouponListDialog />}
      {props.packs.map(pack => {
        const tierValue = pack.values[props.selectedTier]
        const baseValue = pack.values[0].value
        const purchasable = props.currentTier === props.selectedTier
        return (
          <RegularPackCard
            key={pack.id}
            baseCreditsLabel={formatLocalizedNumber(baseValue, locale)}
            baseCredits={baseValue}
            boostedCredits={tierValue.creditsAfterBoosts}
            isBoosted={baseValue !== tierValue.creditsAfterBoosts}
            boosts={sortBoostsPriority(tierValue.boosts ?? []) ?? []}
            priceLabel={formatPrice(currency, pack.prices[currency])}
            locale={locale}
            animateCredits={props.currentTier === 0}
            disabled={!purchasable && Boolean(props.onRequireUpgradeClick)}
            showSuccess={props.showingSuccessId === pack.id}
            onPurchase={
              purchasable
                ? () => props.onPurchase(pack)
                : props.onRequireUpgradeClick
            }
          />
        )
      })}
    </>
  )
}
